import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../config/supabase';
import { formatAsYouType, formatPhone } from '../../lib/phone';
import { phoneForSave } from '../../lib/phoneSave';

const EMPLOYMENT_TYPES = [
    { value: '', label: 'Unclassified' },
    { value: 'w2_staff', label: 'W-2 staff' },
    { value: '1099_contractor', label: '1099 contractor' }
];

const APPLICANT_STATUSES = [
    { value: 'interview', label: 'Interview' },
    { value: 'green_lit', label: 'Green-Lit' },
    { value: 'invited', label: 'Invited' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'compliance', label: 'Training & Compliance' },
    { value: 'active', label: 'Active' }
];

const UI_STATUS_FLOW = ['invited', 'onboarding', 'compliance', 'active'];
const STAGE_FIELDS = {
    invited: 'invited_at',
    onboarding: 'onboarding_at',
    compliance: 'compliance_at',
    active: 'active_at'
};

const emptyApplicantForm = (entityId = '') => ({
    entity_id: entityId,
    group_id: '',
    hiring_lead_staff_id: '',
    candidate_name: '',
    candidate_email: '',
    candidate_mobile: '',
    title: '',
    employment_type: '',
    status: 'invited',
    selected_step_ids: [],
    onboarding_mode: 'guided',
    attestation: false,
    note: ''
});

function normalizeText(value) {
    return String(value || '').trim().toLowerCase();
}

function optionLabel(options, value, fallback = '—') {
    return options.find((option) => option.value === value)?.label || fallback;
}

function staffDisplayName(member) {
    return member?.user_profile?.full_name?.trim() || member?.title || member?.id || 'Staff member';
}

function buildAccessParams(action, entityId = null, groupId = null) {
    const params = { p_module: 'personnel', p_action: action, p_entity_id: entityId };
    if (groupId) params.p_group_id = groupId;
    return params;
}

async function startApplicantCompliance(applicantId) {
    const { data: sessionData } = await supabaseClient.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error('You must be signed in to start applicant compliance.');

    const response = await fetch('/api/vendors/certn/applicant-compliance', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: ['Bearer', token].join(' ')
        },
        body: JSON.stringify({ applicantId })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(payload?.error || 'Applicant compliance could not be started.');
    }
    return payload;
}

function formatStageDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function statusOptionsFor(applicant) {
    if (!applicant) return APPLICANT_STATUSES.filter((status) => UI_STATUS_FLOW.includes(status.value));
    const currentIndex = UI_STATUS_FLOW.indexOf(applicant.status);
    if (currentIndex === -1) return UI_STATUS_FLOW.map((value) => APPLICANT_STATUSES.find((status) => status.value === value));
    return UI_STATUS_FLOW.slice(currentIndex).map((value) => APPLICANT_STATUSES.find((status) => status.value === value));
}

function ApplicantFormModal({
    mode = 'create',
    applicant = null,
    initialValues = null,
    entities = [],
    groups = [],
    staff = [],
    stepDefinitions = [],
    canManualOnboard = false,
    canEditTarget,
    onClose,
    onSave,
    theme = 'light'
}) {
    const dark = theme === 'dark';
    const isEdit = mode === 'edit';
    const [form, setForm] = useState({
        ...emptyApplicantForm(entities[0]?.id || ''),
        ...(initialValues || {}),
        ...(applicant || {}),
        group_id: applicant?.group_id || initialValues?.group_id || '',
        hiring_lead_staff_id: applicant?.hiring_lead_staff_id || '',
        title: applicant?.title || initialValues?.title || '',
        employment_type: applicant?.employment_type || initialValues?.employment_type || ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [pendingFocus, setPendingFocus] = useState('');
    const fieldRefs = useRef({});

    const selectedEntityId = form.entity_id;
    const groupOptions = useMemo(
        () => groups.filter((group) => group.entity_id === selectedEntityId),
        [groups, selectedEntityId]
    );
    const hiringLeadOptions = useMemo(
        () => staff.filter((member) => member.entity_id === selectedEntityId),
        [staff, selectedEntityId]
    );
    const stepOptions = useMemo(
        () => stepDefinitions.filter((step) => step.org_id === entities.find((entity) => entity.id === selectedEntityId)?.org_id),
        [entities, selectedEntityId, stepDefinitions]
    );
    const allowedStatuses = useMemo(() => statusOptionsFor(applicant), [applicant]);

    const inputCls = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
    }`;
    const labelCls = `block text-sm font-medium mb-2 ${dark ? 'text-gray-300' : 'text-gray-700'}`;

    useEffect(() => {
        if (!pendingFocus) return;
        fieldRefs.current[pendingFocus]?.focus();
        setPendingFocus('');
    }, [pendingFocus]);

    const fieldId = (field) => `applicant-field-${field.replace(/[^a-z0-9-]/gi, '-')}`;
    const fieldProps = (field) => ({
        id: fieldId(field),
        ref: (node) => { fieldRefs.current[field] = node; },
        'aria-invalid': Boolean(fieldErrors[field]),
        'aria-describedby': fieldErrors[field] ? `${fieldId(field)}-error` : undefined,
        className: `${inputCls}${fieldErrors[field] ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`,
        style: fieldErrors[field] ? { borderColor: '#ef4444', outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined,
    });
    const fieldError = (field) => fieldErrors[field]
        ? <p id={`${fieldId(field)}-error`} className="mt-1 text-xs font-medium text-red-600" role="alert">{fieldErrors[field]}</p>
        : null;
    const clearFieldError = (field) => {
        setFieldErrors((current) => {
            if (!current[field]) return current;
            const nextErrors = { ...current };
            delete nextErrors[field];
            return nextErrors;
        });
        if (pendingFocus === field) setPendingFocus('');
    };
    const showValidation = (errors) => {
        setError(null);
        setFieldErrors(errors);
        const firstField = Object.keys(errors)[0];
        if (!firstField) return false;
        setPendingFocus(firstField);
        return true;
    };
    const set = (field, value) => {
        clearFieldError(field);
        setError(null);
        setForm((prev) => ({ ...prev, [field]: value }));
    };
    const handleEntityChange = (value) => {
        clearFieldError('entity_id');
        clearFieldError('group_id');
        clearFieldError('hiring_lead_staff_id');
        setError(null);
        setForm((prev) => ({ ...prev, entity_id: value, group_id: '', hiring_lead_staff_id: '' }));
    };

    const handleSubmit = async () => {
        setError(null);
        const errors = {};
        if (!form.entity_id) errors.entity_id = 'Choose the entity this applicant belongs to.';
        if (form.entity_id && !canEditTarget(form.entity_id, form.group_id || null)) {
            const hasAssignableDepartment = groups.some((group) =>
                group.entity_id === form.entity_id && canEditTarget(form.entity_id, group.id));
            errors[form.group_id || hasAssignableDepartment ? 'group_id' : 'entity_id'] =
                'Choose a placement within your Personnel edit scope.';
        }
        if (!isEdit) {
            if (!form.candidate_name.trim()) errors.candidate_name = 'Candidate name is required.';
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.candidate_email.trim())) errors.candidate_email = 'Enter a valid candidate email.';
            if (!form.candidate_mobile.trim()) errors.candidate_mobile = 'Candidate mobile is required.';
            if (form.onboarding_mode === 'guided' && form.selected_step_ids.length === 0)
                errors.selected_step_ids = 'Choose at least one driver onboarding step for this application.';
            if (form.onboarding_mode === 'manual') {
                if (!canManualOnboard) errors.onboarding_mode = 'Manual onboarding requires organization admin or owner access.';
                if (!form.attestation) errors.attestation = 'Verify the offline documents.';
                if (!form.note.trim()) errors.note = 'Enter a required note.';
                if (!form.title.trim()) errors.title = 'Title is required for manual onboarding.';
                if (!form.employment_type) errors.employment_type = 'Employment type is required for manual onboarding.';
            }
        }
        const normalizedTitle = form.title.trim();
        if (form.status === 'active' && !normalizedTitle) errors.title = 'Title is required before an applicant can become Active.';
        if (showValidation(errors)) return;
        const payload = isEdit
            ? {
                title: normalizedTitle || null,
                employment_type: form.employment_type || null,
                status: form.status
            }
            : {
                entity_id: form.entity_id,
                group_id: form.group_id || null,
                hiring_lead_staff_id: form.hiring_lead_staff_id || null,
                candidate_name: form.candidate_name.trim(),
                candidate_email: form.candidate_email.trim(),
                candidate_mobile: phoneForSave(form.candidate_mobile),
                resulting_staff_id: form.resulting_staff_id || null,
                selected_step_ids: form.selected_step_ids,
                onboarding_mode: form.onboarding_mode,
                attestation: form.attestation,
                note: form.note.trim(),
                title: normalizedTitle || null,
                employment_type: form.employment_type || null,
                status: 'invited'
            };
        try {
            setSaving(true);
            await onSave(payload, form);
        } catch (e) {
            setFieldErrors({});
            setError(e?.message || 'Something went wrong while saving.');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${dark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <h2 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>
                        {isEdit ? '✏️ Edit Applicant' : '➕ Onboard Applicant'}
                    </h2>
                    <button onClick={onClose} className={`text-2xl ${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>×</button>
                </div>

                <div className="p-6 space-y-5">
                    {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className={labelCls}>Company/Market <span className="required-asterisk">*</span></label>
                            <select {...fieldProps('entity_id')} value={form.entity_id} onChange={(e) => handleEntityChange(e.target.value)} disabled={isEdit}>
                                <option value="">Choose an entity</option>
                                {entities.map((entity) => (
                                    <option key={entity.id} value={entity.id}>{entity.name}{entity.display_label ? ` (${entity.display_label})` : ''}</option>
                                ))}
                            </select>
                            {fieldError('entity_id')}
                        </div>
                        <div>
                            <label className={labelCls}>Department</label>
                            <select {...fieldProps('group_id')} value={form.group_id} onChange={(e) => set('group_id', e.target.value)} disabled={isEdit || !selectedEntityId}>
                                <option value="">— No Department —</option>
                                {groupOptions.map((group) => (
                                    <option key={group.id} value={group.id}>{group.name}{group.group_type ? ` (${group.group_type})` : ''}</option>
                                ))}
                            </select>
                            {fieldError('group_id')}
                        </div>

                        {!isEdit && (
                            <>
                                <div className="md:col-span-2">
                                    <label className={labelCls}>Onboarding mode</label>
                                    <select {...fieldProps('onboarding_mode')} aria-label="Onboarding mode" value={form.onboarding_mode} onChange={(e) => {
                                        ['selected_step_ids', 'attestation', 'note', 'title', 'employment_type'].forEach(clearFieldError);
                                        set('onboarding_mode', e.target.value);
                                    }}>
                                        <option value="guided">Guided</option>
                                        <option value="manual" disabled={!canManualOnboard}>Manual (admin/owner only)</option>
                                    </select>
                                    {fieldError('onboarding_mode')}
                                </div>
                                {form.onboarding_mode === 'manual' && (
                                    <>
                                        <div>
                                            <label className={labelCls}>Title <span className="required-asterisk">*</span></label>
                                            <input {...fieldProps('title')} aria-label="Manual job/title" value={form.title} onChange={(e) => set('title', e.target.value)} />
                                            {fieldError('title')}
                                        </div>
                                        <div>
                                            <label className={labelCls}>Employment type <span className="required-asterisk">*</span></label>
                                            <select {...fieldProps('employment_type')} aria-label="Manual employment type" value={form.employment_type} onChange={(e) => set('employment_type', e.target.value)}>
                                                {EMPLOYMENT_TYPES.map((type) => <option key={type.value || 'blank'} value={type.value}>{type.label}</option>)}
                                            </select>
                                            {fieldError('employment_type')}
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className={labelCls}>
                                                <input ref={(node) => { fieldRefs.current.attestation = node; }} id={fieldId('attestation')}
                                                    aria-invalid={Boolean(fieldErrors.attestation)}
                                                    aria-describedby={fieldErrors.attestation ? `${fieldId('attestation')}-error` : undefined}
                                                    className={fieldErrors.attestation ? 'outline outline-2 outline-red-500' : ''}
                                                    style={fieldErrors.attestation ? { outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined}
                                                    type="checkbox" checked={form.attestation} onChange={(e) => set('attestation', e.target.checked)} /> I verified this driver's documents offline
                                            </label>
                                            {fieldError('attestation')}
                                            <label className={labelCls}>Required note</label>
                                            <textarea {...fieldProps('note')} aria-label="Manual onboarding note" value={form.note} onChange={(e) => set('note', e.target.value)} />
                                            {fieldError('note')}
                                            <p className="text-xs mt-1">Manual onboarding creates an Active driver and records your verification. The driver receives an invite to verify a code and create their own password.</p>
                                        </div>
                                    </>
                                )}
                                <div>
                                    <label className={labelCls}>Hiring lead / manager</label>
                                    <select {...fieldProps('hiring_lead_staff_id')} value={form.hiring_lead_staff_id} onChange={(e) => set('hiring_lead_staff_id', e.target.value)} disabled={!selectedEntityId}>
                                        <option value="">— No hiring lead yet —</option>
                                        {hiringLeadOptions.map((member) => (
                                            <option key={member.id} value={member.id}>{staffDisplayName(member)}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Candidate name <span className="required-asterisk">*</span></label>
                                    <input {...fieldProps('candidate_name')} type="text" value={form.candidate_name} onChange={(e) => set('candidate_name', e.target.value)} placeholder="e.g. Riley Candidate" />
                                    {fieldError('candidate_name')}
                                </div>
                                <div>
                                    <label className={labelCls}>Candidate email <span className="required-asterisk">*</span></label>
                                    <input {...fieldProps('candidate_email')} type="email" value={form.candidate_email} onChange={(e) => set('candidate_email', e.target.value)} placeholder="riley@example.com" />
                                    {fieldError('candidate_email')}
                                </div>
                                <div>
                                    <label className={labelCls}>Candidate mobile <span className="required-asterisk">*</span></label>
                                    <input {...fieldProps('candidate_mobile')} type="tel" inputMode="tel" autoComplete="tel" value={form.candidate_mobile} onChange={(e) => set('candidate_mobile', formatAsYouType(e.target.value).text)} placeholder="(555) 010-1234" />
                                    {fieldError('candidate_mobile')}
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelCls}>Driver onboarding steps {form.onboarding_mode === 'guided' ? <span className="required-asterisk">*</span> : '(optional)'}</label>
                                    <fieldset ref={(node) => { fieldRefs.current.selected_step_ids = node; }} tabIndex={-1}
                                        aria-invalid={Boolean(fieldErrors.selected_step_ids)}
                                        style={fieldErrors.selected_step_ids ? { borderColor: '#ef4444', outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined}
                                        className={`grid grid-cols-1 md:grid-cols-2 gap-2 rounded-lg border p-3 ${fieldErrors.selected_step_ids ? 'border-red-500' : dark ? 'border-gray-600' : 'border-gray-200'}`}>
                                        <legend className="sr-only">Driver onboarding steps</legend>
                                        {stepOptions.map((step) => (
                                            <label key={step.id} className={`flex items-center gap-2 text-sm ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.selected_step_ids.includes(step.id)}
                                                    onChange={(e) => {
                                                        clearFieldError('selected_step_ids');
                                                        setError(null);
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            selected_step_ids: e.target.checked
                                                                ? [...prev.selected_step_ids, step.id]
                                                                : prev.selected_step_ids.filter((id) => id !== step.id)
                                                        }));
                                                    }}
                                                />
                                                <span>{step.label}</span>
                                            </label>
                                        ))}
                                        {stepOptions.length === 0 && (
                                            <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>No active driver onboarding steps are configured for this tenant yet.</p>
                                        )}
                                    </fieldset>
                                    {fieldError('selected_step_ids')}
                                </div>
                                <p className="md:col-span-2 text-sm">New drivers receive an invite and a one-time code, then create their own password.</p>
                            </>
                        )}

                        {isEdit && (
                            <>
                                <div>
                                    <label className={labelCls}>Title</label>
                                    <input {...fieldProps('title')} type="text" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Chauffeur" />
                                    {fieldError('title')}
                                    <p className={`text-xs mt-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Single surviving job/title label mirrored into staff when this applicant becomes Active.</p>
                                </div>
                                <div>
                                    <label className={labelCls}>Employment type</label>
                                    <select {...fieldProps('employment_type')} value={form.employment_type} onChange={(e) => set('employment_type', e.target.value)}>
                                        {EMPLOYMENT_TYPES.map((type) => <option key={type.value || 'blank'} value={type.value}>{type.label}</option>)}
                                    </select>
                                    {fieldError('employment_type')}
                                </div>
                                <div>
                                    <label className={labelCls}>Status</label>
                                    <select {...fieldProps('status')} value={form.status} onChange={(e) => set('status', e.target.value)}>
                                        {allowedStatuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                                    </select>
                                    {fieldError('status')}
                                    <p className={`text-xs mt-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Status can only move forward through Invited → Onboarding → Compliance → Active.</p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className={`sticky bottom-0 border-t px-6 py-4 flex justify-between gap-3 ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>{isEdit ? 'Active conversion creates a staff row with no platform login.' : form.onboarding_mode === 'manual' ? 'Manual onboarding saves the applicant, Active driver, and audit note together.' : 'Send Invitation records the applicant, selected driver steps, and first-login reset requirement.'}</p>
                    <div className="flex gap-3">
                        <button onClick={onClose} disabled={saving} className={`px-4 py-2 rounded-lg font-medium border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}>Cancel</button>
                        <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50">
                            {saving ? 'Saving…' : isEdit ? 'Save Changes' : form.onboarding_mode === 'manual' ? 'Mark Active (manual onboarding)' : 'Send Invitation'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ApplicantPipelineView({ userId, theme = 'light' }) {
    const location = useLocation();
    const navigate = useNavigate();
    const prefillHandled = useRef(false);
    const [driverInvitePrefill, setDriverInvitePrefill] = useState(() => location.state?.bl387DriverInvite || null);
    const dark = theme === 'dark';
    const [applicants, setApplicants] = useState([]);
    const [entities, setEntities] = useState([]);
    const [orgGroups, setOrgGroups] = useState([]);
    const [staff, setStaff] = useState([]);
    const [stepDefinitions, setStepDefinitions] = useState([]);
    const [entityEditAccess, setEntityEditAccess] = useState({});
    const [groupEditAccess, setGroupEditAccess] = useState({});
    const [canEditAll, setCanEditAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [entityFilter, setEntityFilter] = useState('');
    const [groupFilter, setGroupFilter] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [editing, setEditing] = useState(null);
    const [canManualOnboard, setCanManualOnboard] = useState(false);
    const [loginApplicant, setLoginApplicant] = useState(null);
    const [loginError, setLoginError] = useState(null);
    const [loginSaving, setLoginSaving] = useState(false);
    const [notice, setNotice] = useState(null);
    const [pendingManualLogins, setPendingManualLogins] = useState(new Set());

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        const [entitiesResult, groupsResult, staffResult, applicantsResult, stepsResult, pendingResult] = await Promise.all([
            supabaseClient
                .from('entities')
                .select('id, org_id, name, parent_entity_id, display_label')
                .order('name'),
            supabaseClient
                .from('org_groups')
                .select('id, org_id, entity_id, parent_group_id, group_type, name, created_at')
                .order('created_at'),
            supabaseClient
                .from('staff')
                .select('id, org_id, entity_id, group_id, employment_type, badge, direction, title, status, user_profile_id, user_profile:user_profiles(id, full_name), created_at')
                .order('created_at', { ascending: false }),
            supabaseClient
                .from('applicants')
                .select('id, org_id, entity_id, group_id, hiring_lead_staff_id, candidate_name, candidate_email, candidate_mobile, title, employment_type, status, interview_at, green_lit_at, invited_at, onboarding_at, compliance_at, active_at, resulting_staff_id, created_at')
                .order('created_at', { ascending: false }),
            supabaseClient
                .from('onboarding_step_definitions')
                .select('id, org_id, key, label, is_active')
                .eq('is_active', true)
                .eq('context', 'driver')
                .order('label'),
            supabaseClient.rpc('bl388_pending_manual_logins')
        ]);
        setPendingManualLogins(new Set(Array.isArray(pendingResult.data) ? pendingResult.data.map((row) => row.id) : []));
        if (pendingResult.error) setNotice(`Applicants needing login could not be checked: ${pendingResult.error.message}`);

        const firstError = entitiesResult.error || groupsResult.error || staffResult.error || applicantsResult.error || stepsResult.error;
        if (firstError) {
            setError(firstError.message);
            setApplicants([]);
            setEntities([]);
            setOrgGroups([]);
            setStaff([]);
            setStepDefinitions([]);
            setEntityEditAccess({});
            setGroupEditAccess({});
            setCanEditAll(false);
            setLoading(false);
            return;
        }

        const allEntities = entitiesResult.data || [];
        const allGroups = groupsResult.data || [];
        const allStaff = staffResult.data || [];
        const allApplicants = applicantsResult.data || [];
        const allSteps = stepsResult.data || [];
        const [fullViewResult, fullEditResult, manualResult] = await Promise.all([
            supabaseClient.rpc('has_module_access', buildAccessParams('view')),
            supabaseClient.rpc('has_module_access', buildAccessParams('edit')),
            supabaseClient.rpc('bl388_can_manual_onboard')
        ]);
        setCanManualOnboard(!manualResult.error && manualResult.data === true);
        if (manualResult.error) setNotice(`Manual onboarding access could not be checked: ${manualResult.error.message}`);
        const hasFullView = !fullViewResult.error && fullViewResult.data === true;
        const hasFullEdit = !fullEditResult.error && fullEditResult.data === true;
        const viewEntityIds = new Set();
        const editEntityIds = new Set();
        const viewGroupIds = new Set();
        const editGroupIds = new Set();

        if (hasFullView) {
            allEntities.forEach((entity) => viewEntityIds.add(entity.id));
            allGroups.forEach((group) => viewGroupIds.add(group.id));
            if (hasFullEdit) {
                allEntities.forEach((entity) => editEntityIds.add(entity.id));
                allGroups.forEach((group) => editGroupIds.add(group.id));
            }
        } else {
            const [entityAccessResults, groupAccessResults] = await Promise.all([
                Promise.all(allEntities.map(async (entity) => {
                    const [viewResult, editResult] = await Promise.all([
                        supabaseClient.rpc('has_module_access', buildAccessParams('view', entity.id)),
                        supabaseClient.rpc('has_module_access', buildAccessParams('edit', entity.id))
                    ]);
                    return {
                        entity,
                        canView: !viewResult.error && viewResult.data === true,
                        canEdit: !editResult.error && editResult.data === true
                    };
                })),
                Promise.all(allGroups.map(async (group) => {
                    const [viewResult, editResult] = await Promise.all([
                        supabaseClient.rpc('has_module_access', buildAccessParams('view', group.entity_id, group.id)),
                        supabaseClient.rpc('has_module_access', buildAccessParams('edit', group.entity_id, group.id))
                    ]);
                    return {
                        group,
                        canView: !viewResult.error && viewResult.data === true,
                        canEdit: !editResult.error && editResult.data === true
                    };
                }))
            ]);
            entityAccessResults.forEach((result) => {
                if (result.canView) viewEntityIds.add(result.entity.id);
                if (result.canEdit) editEntityIds.add(result.entity.id);
            });
            groupAccessResults.forEach((result) => {
                if (result.canView) viewGroupIds.add(result.group.id);
                if (result.canEdit) editGroupIds.add(result.group.id);
            });
        }

        const visibleApplicants = hasFullView
            ? allApplicants
            : allApplicants.filter((applicant) => (
                viewEntityIds.has(applicant.entity_id)
                || (applicant.group_id && viewGroupIds.has(applicant.group_id))
            ));
        const visibleEntityIds = new Set([
            ...visibleApplicants.map((applicant) => applicant.entity_id),
            ...viewEntityIds,
            ...allGroups.filter((group) => viewGroupIds.has(group.id) || editGroupIds.has(group.id)).map((group) => group.entity_id)
        ]);
        const editableEntityIds = new Set([
            ...editEntityIds,
            ...allGroups.filter((group) => editGroupIds.has(group.id)).map((group) => group.entity_id)
        ]);
        const visibleGroups = allGroups.filter((group) => (
            hasFullView
            || viewGroupIds.has(group.id)
            || editGroupIds.has(group.id)
            || viewEntityIds.has(group.entity_id)
        ));

        setApplicants(visibleApplicants);
        setEntities(allEntities.filter((entity) => hasFullView || visibleEntityIds.has(entity.id) || editableEntityIds.has(entity.id)));
        setOrgGroups(visibleGroups);
        setStaff(allStaff.filter((member) => hasFullView || visibleEntityIds.has(member.entity_id) || editableEntityIds.has(member.entity_id)));
        setStepDefinitions(allSteps.filter((step) => entitiesResult.data.some((entity) => entity.org_id === step.org_id)));
        setEntityEditAccess(Object.fromEntries([...editEntityIds].map((id) => [id, true])));
        setGroupEditAccess(Object.fromEntries([...editGroupIds].map((id) => [id, true])));
        setCanEditAll(hasFullEdit);
        setLoading(false);
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData, userId]);

    useEffect(() => {
        if (!driverInvitePrefill || prefillHandled.current
            || !entities.some((entity) => entity.id === driverInvitePrefill.entity_id)) return;
        prefillHandled.current = true;
        setEditing(null);
        setFormMode('create');
        setShowForm(true);
        navigate(location.pathname, { replace: true, state: null });
    }, [driverInvitePrefill, entities, location.pathname, navigate]);

    const entityMap = useMemo(() => new Map(entities.map((entity) => [entity.id, entity])), [entities]);
    const groupMap = useMemo(() => new Map(orgGroups.map((group) => [group.id, group])), [orgGroups]);
    const staffMap = useMemo(() => new Map(staff.map((member) => [member.id, member])), [staff]);

    const entityLabel = useCallback((entityId) => entityMap.get(entityId)?.name || 'Unknown entity', [entityMap]);
    const groupLabel = useCallback((groupId) => groupMap.get(groupId)?.name || 'No department', [groupMap]);
    const hiringLeadLabel = useCallback((staffId) => (staffId ? staffDisplayName(staffMap.get(staffId)) : 'No hiring lead'), [staffMap]);
    const canEditTarget = useCallback((entityId, groupId = null) => (
        canEditAll
        || Boolean(entityEditAccess[entityId])
        || Boolean(groupId && groupEditAccess[groupId])
    ), [canEditAll, entityEditAccess, groupEditAccess]);
    const canCreateApplicant = canEditAll || Object.keys(entityEditAccess).length > 0 || Object.keys(groupEditAccess).length > 0;

    const filteredApplicants = useMemo(() => {
        const needle = normalizeText(query);
        return applicants.filter((applicant) => {
            const haystack = normalizeText([
                applicant.candidate_name,
                applicant.candidate_email,
                applicant.candidate_mobile,
                formatPhone(applicant.candidate_mobile, 'US'),
                applicant.title,
                entityLabel(applicant.entity_id),
                groupLabel(applicant.group_id),
                hiringLeadLabel(applicant.hiring_lead_staff_id)
            ].join(' '));
            return (!needle || haystack.includes(needle))
                && (!statusFilter || applicant.status === statusFilter)
                && (!entityFilter || applicant.entity_id === entityFilter)
                && (!groupFilter || (applicant.group_id || '') === groupFilter);
        });
    }, [applicants, query, statusFilter, entityFilter, groupFilter, entityLabel, groupLabel, hiringLeadLabel]);

    const formEntities = useMemo(
        () => entities.filter((entity) => canEditAll || entityEditAccess[entity.id] || orgGroups.some((group) => group.entity_id === entity.id && groupEditAccess[group.id])),
        [entities, orgGroups, canEditAll, entityEditAccess, groupEditAccess]
    );
    const formGroups = useMemo(
        () => orgGroups.filter((group) => canEditAll || entityEditAccess[group.entity_id] || groupEditAccess[group.id]),
        [orgGroups, canEditAll, entityEditAccess, groupEditAccess]
    );

    const openCreate = () => {
        setEditing(null);
        setFormMode('create');
        setShowForm(true);
    };
    const openEdit = (applicant) => {
        setEditing(applicant);
        setFormMode('edit');
        setShowForm(true);
    };
    const closeForm = () => {
        setEditing(null);
        setShowForm(false);
        setDriverInvitePrefill(null);
    };

    const createApplicant = async (payload) => {
        const { selected_step_ids, ...record } = payload;
        const { data: applicantId, error: saveError } = await supabaseClient.rpc('bl388_create_applicant', {
            p_record: record,
            p_step_ids: selected_step_ids || []
        });
        if (saveError) throw saveError;
        if (!applicantId) throw new Error('The applicant save returned no record. Reload before retrying.');
        closeForm();
        setNotice(payload.onboarding_mode === 'manual' ? 'Manual onboarding saved. The driver is Active.' : 'Applicant and onboarding steps saved.');
        await loadData();
        const savedApplicant = { ...record, id: applicantId };
        setLoginApplicant(savedApplicant);
        await provisionLogin(savedApplicant);
    };

    const provisionLogin = async (applicant) => {
        setLoginError(null);
        setLoginSaving(true);
        try {
            const { data, error: inviteError } = await supabaseClient.functions.invoke('driver-application-auth', {
                body: { action: 'provision_login', applicantId: applicant.id }
            });
            if (inviteError) {
                const body = typeof Response !== 'undefined' && inviteError.context instanceof Response ? await inviteError.context.json() : null;
                throw new Error(body?.error || inviteError.message);
            }
            if (data?.ok !== true) throw new Error(data?.error || 'Login provisioning did not confirm success.');
            setLoginApplicant(null);
            setNotice('Applicant invite sent. New users verify a code and create their password. Existing passwords are not reset.');
            await loadData();
        } catch (e) {
            setLoginError(`Applicant is saved. Login is not confirmed: ${e.message || 'Request failed.'} Retry login here; do not create another applicant.`);
        } finally {
            setLoginSaving(false);
        }
    };

    const updateApplicant = async (payload) => {
        const startsCompliance = payload.status === 'compliance' && editing.status !== 'compliance';
        const updatePayload = { ...payload, updated_by: userId };
        if (payload.status !== editing.status && STAGE_FIELDS[payload.status] && !editing[STAGE_FIELDS[payload.status]]) {
            updatePayload[STAGE_FIELDS[payload.status]] = new Date().toISOString();
        }
        let shouldQueueHireNotification = false;
        if (payload.status === 'active' && editing.status !== 'active' && !editing.resulting_staff_id) {
            const { data: staffId, error: staffError } = await supabaseClient.rpc('bl391_convert_applicant_staff', {
                p_applicant_id: editing.id, p_record: payload
            });
            if (staffError) throw staffError;
            if (!staffId) throw new Error('Applicant staff conversion did not return a record.');
            updatePayload.resulting_staff_id = staffId;
            const { data: onboardingRecord, error: onboardingError } = await supabaseClient
                .from('onboarding_records')
                .select('id, driver_id')
                .eq('applicant_id', editing.id)
                .maybeSingle();
            if (onboardingError) throw onboardingError;
            if (onboardingRecord) {
                shouldQueueHireNotification = true;
                if (!onboardingRecord.driver_id) {
                    const { data: driverRow, error: driverError } = await supabaseClient
                        .from('drivers')
                        .insert([{
                            org_id: editing.org_id,
                            entity_id: editing.entity_id,
                            user_profile_id: null,
                            onboarding_id: onboardingRecord.id,
                            staff_id: updatePayload.resulting_staff_id,
                            employment_classification: payload.employment_type === '1099_contractor' ? '1099_affiliate' : 'w2_staff',
                            created_by: userId
                        }])
                        .select('id')
                        .single();
                    if (driverError) throw driverError;
                    const { error: onboardingLinkError } = await supabaseClient
                        .from('onboarding_records')
                        .update({ driver_id: driverRow?.id || null })
                        .eq('id', onboardingRecord.id);
                    if (onboardingLinkError) throw onboardingLinkError;
                }
            }
        }
        const { error: saveError } = await supabaseClient
            .from('applicants')
            .update(updatePayload)
            .eq('id', editing.id)
            .select()
            .single();
        if (saveError) throw saveError;
        if (shouldQueueHireNotification) {
            supabaseClient.rpc('bl353_queue_hire_notification', { p_applicant_id: editing.id }).catch(() => {});
        }
        if (startsCompliance) {
            await startApplicantCompliance(editing.id);
        }
        closeForm();
        await loadData();
    };

    const cardCls = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const inputCls = `px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
    }`;

    if (loading) return <div className="p-10 text-center text-gray-500">Loading applicant pipeline…</div>;
    if (error) return <div className="p-6 text-center text-red-600">Could not load applicants: {error}</div>;

    return (
        <div className="h-full flex flex-col">
            {notice && <div role="status" className="mb-3 p-3 border rounded-lg text-sm">{notice}</div>}
            <div className="mb-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                    <h1 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>🧭 Applicant Pipeline</h1>
                    <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Track invited applicants through onboarding, compliance, and active staff conversion.
                    </p>
                </div>
                {canCreateApplicant && (
                    <button onClick={openCreate} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium">
                        Onboard Applicant
                    </button>
                )}
            </div>

            <div className={`rounded-xl border ${cardCls} p-4 mb-4`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name, email, entity, department…"
                        className={`${inputCls} md:col-span-1`}
                    />
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
                        <option value="">All statuses</option>
                        {APPLICANT_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                    </select>
                    <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className={inputCls}>
                        <option value="">All visible entities</option>
                        {entities.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}
                    </select>
                    <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className={inputCls}>
                        <option value="">All departments</option>
                        {orgGroups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                    </select>
                </div>
            </div>

            <div className={`rounded-xl border ${cardCls} overflow-hidden flex-1`}>
                <div className={`px-4 py-3 border-b flex items-center justify-between ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div>
                        <h2 className={`font-bold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>Applicant board</h2>
                        <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>{filteredApplicants.length} visible record{filteredApplicants.length === 1 ? '' : 's'} · new logins receive an invite and create their own password after code verification</p>
                    </div>
                </div>

                {filteredApplicants.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="text-4xl mb-2">🧭</div>
                        <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No applicants match this view</div>
                        <div className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Try a different search/filter, or onboard an applicant if you have Personnel edit access.</div>
                    </div>
                ) : (
                    <div className="overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className={dark ? 'bg-gray-900/50' : 'bg-gray-50'}>
                                <tr>
                                    {['Candidate', 'Status', 'Company/Market', 'Department', 'Hiring Lead', 'Title', 'Stage Timestamps', 'Actions'].map((heading) => (
                                        <th key={heading} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{heading}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {filteredApplicants.map((applicant) => {
                                    const canEdit = canEditTarget(applicant.entity_id, applicant.group_id);
                                    const reachedStages = UI_STATUS_FLOW
                                        .map((status) => ({ status, date: formatStageDate(applicant[STAGE_FIELDS[status]]) }))
                                        .filter((stage) => stage.date);
                                    return (
                                        <tr key={applicant.id} className={dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                            <td className={`px-4 py-3 text-sm font-medium ${dark ? 'text-gray-100' : 'text-gray-900'}`}>
                                                <div>{applicant.candidate_name}</div>
                                                <div className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>{applicant.candidate_email} · {formatPhone(applicant.candidate_mobile, 'US')}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                                                    {optionLabel(APPLICANT_STATUSES, applicant.status, applicant.status)}
                                                </span>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{entityLabel(applicant.entity_id)}</td>
                                            <td className={`px-4 py-3 text-sm ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{groupLabel(applicant.group_id)}</td>
                                            <td className={`px-4 py-3 text-sm ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{hiringLeadLabel(applicant.hiring_lead_staff_id)}</td>
                                            <td className={`px-4 py-3 text-sm ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <div>{applicant.title || 'Title pending'}</div>
                                                <div className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>{optionLabel(EMPLOYMENT_TYPES, applicant.employment_type || '')}</div>
                                            </td>
                                            <td className={`px-4 py-3 text-xs ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {reachedStages.length === 0 ? 'No stage timestamps yet' : reachedStages.map((stage) => (
                                                    <div key={stage.status}>{optionLabel(APPLICANT_STATUSES, stage.status)}: {stage.date}</div>
                                                ))}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {canEdit ? (
                                                    <button onClick={() => openEdit(applicant)} className={`px-3 py-1.5 rounded text-sm font-medium border ${dark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Edit</button>
                                                ) : (
                                                    <span className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>View only</span>
                                                )}
                                                {canEdit && (applicant.status === 'invited' || pendingManualLogins.has(applicant.id)) && (
                                                    <button onClick={() => { setLoginApplicant(applicant); setLoginError(null); }} className="ml-2 px-3 py-1.5 rounded border">Retry login</button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showForm && (
                <ApplicantFormModal
                    mode={formMode}
                    applicant={editing}
                    initialValues={formMode === 'create' ? driverInvitePrefill : null}
                    entities={formEntities}
                    groups={formGroups}
                    staff={staff}
                    stepDefinitions={stepDefinitions}
                    canManualOnboard={canManualOnboard}
                    canEditTarget={canEditTarget}
                    onClose={closeForm}
                    onSave={formMode === 'edit' ? updateApplicant : createApplicant}
                    theme={theme}
                />
            )}
            {loginApplicant && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div role="dialog" aria-label="Retry applicant login" className="bg-[var(--surface-raised)] text-[var(--ink)] p-6 rounded-lg max-w-lg w-full space-y-4">
                        <h2 className="text-xl font-bold">Retry login: {loginApplicant.candidate_name}</h2>
                        <p className="text-sm">The applicant is already saved. This only provisions their login; it never creates another applicant.</p>
                        {loginError && <p role="alert" className="text-red-600">{loginError}</p>}
                        <p>The invite opens the code verification and password creation flow. No admin-set password is needed.</p>
                        <button disabled={loginSaving} onClick={() => setLoginApplicant(null)} className="border rounded px-3 py-2">Close</button>
                        <button disabled={loginSaving} onClick={() => provisionLogin(loginApplicant)} className="ml-2 bg-orange-600 text-white rounded px-3 py-2">{loginSaving ? 'Saving…' : 'Retry login'}</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ApplicantPipelineView;
